import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { getBillingSummary, getCases } from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import {
  DollarSign, AlertTriangle, ChevronRight, Search, RefreshCw, Briefcase,
} from 'lucide-react';

const fmtMoney = (n) => {
  const num = parseFloat(n) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const statusColor = (s) => {
  const map = {
    open:'#7c3aed', verified:'#1d4ed8', negotiation_needed:'#c2410c',
    records_received:'#15803d', paid:'#15803d', waived:'#6b7280',
    underpaid:'#c2410c', disputed:'#a855f7', pending:'#9ca3af',
    not_started:'#3b82f6', suspended:'#ef4444',
  };
  return map[s] || '#9ca3af';
};
const statusBg  = (s) => statusColor(s) + '15';
const statusLbl = (s) => (s||'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());

function KpiCard({ label, value, color, sub, icon: Icon }) {
  return (
    <div className="card" style={{ padding:'16px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'8px' }}>
        <div style={{ fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-muted)' }}>{label}</div>
        {Icon && <Icon size={15} style={{ color:'var(--text-muted)', opacity:0.5 }} />}
      </div>
      <div style={{ fontSize:'1.45rem', fontWeight:800, color, lineHeight:1, marginBottom:'4px' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.73rem', color:'var(--text-muted)', lineHeight:1.4 }}>{sub}</div>}
    </div>
  );
}

export default function BillingPage() {
  const navigate = useNavigate();

  const [summary,    setSummary]    = useState(null);
  const [cases,      setCases]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeTab,  setActiveTab]  = useState('overview');
  const [search,     setSearch]     = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sumRes, casesRes] = await Promise.all([
        getBillingSummary(),
        getCases({ limit: 500 }),
      ]);
      setSummary(sumRes);
      setCases(casesRes.cases || []);
    } catch (e) { console.error('[billing page]', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const piCases = cases.filter(c => {
    const ct = (c.claim_type || '').toUpperCase();
    return ct === 'PI' || ct === 'MO' || ct === 'IND' || ct === 'DTH' || ct === 'DOL' || ct === 'TP';
  });
  const wcCases = cases.filter(c => {
    const ct = (c.claim_type || '').toUpperCase();
    return ct === 'WC' || ct === 'INDEMNITY' || ct === 'MEDICAL ONLY' || c.claim_type?.toLowerCase().includes('worker');
  });

  const q = search.toLowerCase();
  const filteredPi = piCases.filter(c => !q || (c.client_name||'').toLowerCase().includes(q) || (c.case_number||'').toLowerCase().includes(q));
  const filteredWc = wcCases.filter(c => !q || (c.client_name||'').toLowerCase().includes(q) || (c.case_number||'').toLowerCase().includes(q));

  const pi = summary?.pi || {};
  const wc = summary?.wc || {};

  const tabs = [
    { key:'overview', label:'Overview' },
    { key:'pi',       label:`PI Billing (${piCases.length})` },
    { key:'wc',       label:`WC Billing (${wcCases.length})` },
  ];

  // ── shared table styles ────────────────────────────────────────────────────
  const TH = (h) => (
    <th key={h} style={{ padding:'8px 12px', textAlign: h==='Action'?'center':'left', fontSize:'0.67rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{h}</th>
  );

  return (
    <>
      <Header title="Billing" />
      <div className="app-content" style={{ padding:'20px' }}>

        {/* ── Title bar ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
          <div>
            <h1 style={{ fontSize:'1.3rem', fontWeight:700, color:'var(--text-primary)', margin:0 }}>Billing</h1>
            <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginTop:2 }}>
              Firm-wide billing overview across PI and Workers' Comp cases
            </div>
          </div>
          <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
            <div style={{ position:'relative' }}>
              <Search size={14} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }} />
              <input className="form-input" style={{ paddingLeft:30, width:220, fontSize:'0.85rem' }}
                placeholder="Search cases…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="btn btn-ghost btn-sm" onClick={load} style={{ display:'flex', alignItems:'center', gap:5 }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', borderBottom:'2px solid var(--border)', marginBottom:'20px' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding:'9px 22px', background:'none', border:'none', cursor:'pointer',
              fontWeight: activeTab===t.key ? 700 : 500, fontSize:'0.88rem', fontFamily:'inherit',
              color: activeTab===t.key ? 'var(--zeus-gold)' : 'var(--text-muted)',
              borderBottom: activeTab===t.key ? '2px solid var(--zeus-gold)' : '2px solid transparent',
              marginBottom:-2,
            }}>{t.label}</button>
          ))}
        </div>

        {loading && <div style={{ textAlign:'center', padding:'80px', color:'var(--text-muted)' }}>Loading billing data…</div>}

        {/* ════════════════ OVERVIEW ════════════════ */}
        {!loading && activeTab === 'overview' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>

            {/* Top summary cards — PI + WC side by side */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>

              {/* PI card */}
              <div className="card" style={{ padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px' }}>
                  <Briefcase size={16} style={{ color:'var(--zeus-gold)' }} />
                  <span style={{ fontWeight:700, fontSize:'0.95rem' }}>PI Billing</span>
                  <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(212,168,83,0.12)', color:'var(--zeus-gold)', marginLeft:'auto' }}>
                    {piCases.length} Cases
                  </span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                  {[
                    ['Medical Billed',  fmtMoney(pi.med_billed),    'var(--text-primary)'],
                    ['Outstanding',     fmtMoney(pi.med_balance),    pi.med_balance > 0 ? '#ef4444' : '#22c55e'],
                    ['Total Liens',     fmtMoney(pi.lien_total),     '#f97316'],
                    ['Case Costs',      fmtMoney(pi.cost_total),     '#3b82f6'],
                    ['Settlements',     fmtMoney(pi.settled_total),  '#22c55e'],
                    ['Open Bills',      pi.open_bills || 0,          pi.open_bills > 0 ? '#ef4444' : '#22c55e'],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={{ background:'var(--bg-base)', borderRadius:8, padding:'10px 12px', border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{lbl}</div>
                      <div style={{ fontSize:'1rem', fontWeight:800, color }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* WC card */}
              <div className="card" style={{ padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px' }}>
                  <DollarSign size={16} style={{ color:'#3b82f6' }} />
                  <span style={{ fontWeight:700, fontSize:'0.95rem' }}>WC Billing</span>
                  <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(59,130,246,0.1)', color:'#3b82f6', marginLeft:'auto' }}>
                    {wcCases.length} Cases
                  </span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                  {[
                    ['Benefits Paid',    fmtMoney(wc.benefits_paid),  '#7c3aed'],
                    ['Benefits Owed',    fmtMoney(wc.benefits_owed),   wc.benefits_owed > 0 ? '#ef4444' : '#22c55e'],
                    ['Med. Billed',      fmtMoney(wc.med_billed),      'var(--text-primary)'],
                    ['Med. Balance',     fmtMoney(wc.med_balance),      wc.med_balance > 0 ? '#f97316' : '#22c55e'],
                    ['Case Expenses',    fmtMoney(wc.expense_total),    '#3b82f6'],
                    ['Open Offsets',     fmtMoney(wc.lien_total),       wc.lien_total > 0 ? '#f97316' : '#22c55e'],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={{ background:'var(--bg-base)', borderRadius:8, padding:'10px 12px', border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{lbl}</div>
                      <div style={{ fontSize:'1rem', fontWeight:800, color }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Alerts */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
              {[
                { label:'PI Open Bills',            value: pi.open_bills     || 0, color:'#ef4444', sub:'Awaiting payment or negotiation' },
                { label:'PI Open Liens',            value: pi.open_liens     || 0, color:'#f97316', sub:'Liens not yet resolved' },
                { label:'WC Underpaid / Disputed',  value: wc.open_benefits  || 0, color:'#a855f7', sub:'Benefit payment issues' },
                { label:'WC Pending UR',            value: wc.pending_ur     || 0, color:'#3b82f6', sub:'Awaiting utilization review' },
              ].map(({ label, value, color, sub }) => (
                <KpiCard key={label} label={label} value={value} color={value > 0 ? color : '#22c55e'}
                  sub={sub} icon={value > 0 ? AlertTriangle : undefined} />
              ))}
            </div>

            {/* Recent activity */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>

              {/* Recent PI Bills */}
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <div style={{ padding:'13px 16px 10px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontWeight:700, fontSize:'0.9rem' }}>Recent PI Medical Bills</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab('pi')} style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:4 }}>View All <ChevronRight size={12} /></button>
                </div>
                {!(pi.recent_bills||[]).length
                  ? <div style={{ padding:'24px', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>No medical bills yet</div>
                  : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                      <thead><tr style={{ background:'var(--bg-base)' }}>{['Case','Provider','Billed','Balance','Status'].map(TH)}</tr></thead>
                      <tbody>
                        {(pi.recent_bills||[]).map((b, i) => {
                          const bal = (parseFloat(b.billed)||0) - (parseFloat(b.adjusted)||0) - (parseFloat(b.paid)||0);
                          return (
                            <tr key={b.id} onClick={() => navigate(`/cases/${b.case_id}`, { state: { tab: 'billing' } })}
                              style={{ borderTop:'1px solid var(--border)', cursor:'pointer', background:i%2===0?'transparent':'var(--bg-base)' }}
                              onMouseOver={e=>e.currentTarget.style.background='var(--zeus-gold-dim)'}
                              onMouseOut={e=>e.currentTarget.style.background=i%2===0?'transparent':'var(--bg-base)'}>
                              <td style={{ padding:'8px 10px' }}>
                                <div style={{ fontWeight:700, color:'var(--zeus-gold)', fontSize:'0.8rem' }}>{b._caseInfo?.case_number||'—'}</div>
                                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{b._caseInfo?.client_name||''}</div>
                              </td>
                              <td style={{ padding:'8px 10px', fontWeight:600 }}>{b.provider_name||'—'}</td>
                              <td style={{ padding:'8px 10px', fontWeight:700 }}>{fmtMoney(b.billed)}</td>
                              <td style={{ padding:'8px 10px', fontWeight:700, color:bal>0?'#ef4444':'#22c55e' }}>{fmtMoney(bal)}</td>
                              <td style={{ padding:'8px 10px' }}>
                                <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:statusBg(b.status), color:statusColor(b.status) }}>{statusLbl(b.status)}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                }
              </div>

              {/* Recent WC Benefits */}
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <div style={{ padding:'13px 16px 10px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontWeight:700, fontSize:'0.9rem' }}>Recent WC Benefit Payments</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab('wc')} style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:4 }}>View All <ChevronRight size={12} /></button>
                </div>
                {!(wc.recent_benefits||[]).length
                  ? <div style={{ padding:'24px', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>No benefit payments yet</div>
                  : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                      <thead><tr style={{ background:'var(--bg-base)' }}>{['Case','Type','Paid','Owed','Status'].map(TH)}</tr></thead>
                      <tbody>
                        {(wc.recent_benefits||[]).map((b, i) => (
                          <tr key={b.id} onClick={() => navigate(`/cases/${b.case_id}`, { state: { tab: 'billing' } })}
                            style={{ borderTop:'1px solid var(--border)', cursor:'pointer', background:i%2===0?'transparent':'var(--bg-base)' }}
                            onMouseOver={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                            onMouseOut={e=>e.currentTarget.style.background=i%2===0?'transparent':'var(--bg-base)'}>
                            <td style={{ padding:'8px 10px' }}>
                              <div style={{ fontWeight:700, color:'var(--zeus-gold)', fontSize:'0.8rem' }}>{b._caseInfo?.case_number||'—'}</div>
                              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{b._caseInfo?.client_name||''}</div>
                            </td>
                            <td style={{ padding:'8px 10px', fontWeight:700 }}>{b.benefit_type||'—'}</td>
                            <td style={{ padding:'8px 10px', fontWeight:700 }}>{fmtMoney(b.paid)}</td>
                            <td style={{ padding:'8px 10px', fontWeight:700, color:(parseFloat(b.owed)||0)>0?'#ef4444':'var(--text-muted)' }}>{fmtMoney(b.owed)}</td>
                            <td style={{ padding:'8px 10px' }}>
                              <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:statusBg(b.status), color:statusColor(b.status) }}>{statusLbl(b.status)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                }
              </div>
            </div>
          </div>
        )}

        {/* ════════════════ PI BILLING TAB ════════════════ */}
        {!loading && activeTab === 'pi' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px' }}>
              {[
                { label:'Medical Billed',  value:fmtMoney(pi.med_billed),    color:'var(--text-primary)', sub:`${piCases.length} PI cases` },
                { label:'Outstanding',     value:fmtMoney(pi.med_balance),   color:pi.med_balance>0?'#ef4444':'#22c55e', sub:'Billed minus adj. & paid' },
                { label:'Total Liens',     value:fmtMoney(pi.lien_total),    color:'#f97316', sub:`${pi.open_liens||0} open liens` },
                { label:'Case Costs',      value:fmtMoney(pi.cost_total),    color:'#3b82f6', sub:'Firm-advanced expenses' },
                { label:'Settlements',     value:fmtMoney(pi.settled_total), color:'#22c55e', sub:`${pi.settled_count||0} settled cases` },
              ].map(p => <KpiCard key={p.label} {...p} icon={DollarSign} />)}
            </div>

            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.95rem' }}>PI Cases — Billing Overview</div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>Click any case to open its Billing tab</div>
                </div>
                <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(212,168,83,0.12)', color:'var(--zeus-gold)' }}>{filteredPi.length} cases</span>
              </div>
              {filteredPi.length === 0
                ? <div style={{ padding:'40px', textAlign:'center', color:'var(--text-muted)' }}>No PI cases found</div>
                : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>
                    <thead><tr style={{ background:'var(--bg-base)' }}>{['Case #','Claimant','Type','Status','Demand','Settlement','AF Fee',''].map(TH)}</tr></thead>
                    <tbody>
                      {filteredPi.map((c, i) => (
                        <tr key={c._id||c.id} onClick={() => navigate(`/cases/${c._id||c.id}`, { state: { tab: 'billing' } })}
                          style={{ borderTop:'1px solid var(--border)', cursor:'pointer', background:i%2===0?'transparent':'var(--bg-base)' }}
                          onMouseOver={e=>e.currentTarget.style.background='var(--zeus-gold-dim)'}
                          onMouseOut={e=>e.currentTarget.style.background=i%2===0?'transparent':'var(--bg-base)'}>
                          <td style={{ padding:'10px 12px', fontWeight:700, color:'var(--zeus-gold)' }}>{c.case_number||'—'}</td>
                          <td style={{ padding:'10px 12px', fontWeight:600 }}>{c.client_name||'—'}</td>
                          <td style={{ padding:'10px 12px' }}><span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:8, background:'rgba(212,168,83,0.12)', color:'var(--zeus-gold)' }}>{c.claim_type||'—'}</span></td>
                          <td style={{ padding:'10px 12px' }}><span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:8, background:c.status==='settled'?'rgba(34,197,94,0.1)':'rgba(100,116,139,0.1)', color:c.status==='settled'?'#22c55e':'var(--text-muted)', textTransform:'capitalize' }}>{c.status||'—'}</span></td>
                          <td style={{ padding:'10px 12px', fontWeight:700 }}>{c.demand_amount ? fmtMoney(c.demand_amount) : '—'}</td>
                          <td style={{ padding:'10px 12px', fontWeight:700, color:parseFloat(c.settlement_amount)>0?'#22c55e':'var(--text-muted)' }}>{parseFloat(c.settlement_amount)>0 ? fmtMoney(c.settlement_amount) : '—'}</td>
                          <td style={{ padding:'10px 12px', textAlign:'center' }}>{c.af_fee ? <span style={{ fontWeight:700 }}>{c.af_fee}%</span> : <span style={{ color:'var(--text-muted)' }}>—</span>}</td>
                          <td style={{ padding:'10px 12px', textAlign:'center' }}>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.75rem', display:'flex', alignItems:'center', gap:3, margin:'0 auto' }}
                              onClick={e=>{e.stopPropagation();navigate(`/cases/${c._id||c.id}`, { state: { tab: 'billing' } });}}>
                              Open <ChevronRight size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          </div>
        )}

        {/* ════════════════ WC BILLING TAB ════════════════ */}
        {!loading && activeTab === 'wc' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px' }}>
              {[
                { label:'Benefits Paid',       value:fmtMoney(wc.benefits_paid),  color:'#7c3aed', sub:`${wc.open_benefits||0} underpaid/disputed` },
                { label:'Benefits Owed',       value:fmtMoney(wc.benefits_owed),  color:wc.benefits_owed>0?'#ef4444':'#22c55e', sub:'Outstanding benefit amounts' },
                { label:'Medical Billed',      value:fmtMoney(wc.med_billed),     color:'var(--text-primary)', sub:`Balance: ${fmtMoney(wc.med_balance)}` },
                { label:'Case Expenses',       value:fmtMoney(wc.expense_total),  color:'#3b82f6', sub:'Firm-advanced expenses' },
                { label:'Reimbursements',      value:fmtMoney(wc.lien_total),     color:'#f97316', sub:`${wc.pending_ur||0} pending UR` },
              ].map(p => <KpiCard key={p.label} {...p} icon={DollarSign} />)}
            </div>

            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.95rem' }}>WC Cases — Billing Overview</div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>Benefits-first view — click any case to open its WC Billing tab</div>
                </div>
                <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>{filteredWc.length} cases</span>
              </div>
              {filteredWc.length === 0
                ? <div style={{ padding:'40px', textAlign:'center', color:'var(--text-muted)' }}>No WC cases found</div>
                : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>
                    <thead><tr style={{ background:'var(--bg-base)' }}>{['Case #','Claimant','Type','Status','Settlement / Award','AF Fee',''].map(TH)}</tr></thead>
                    <tbody>
                      {filteredWc.map((c, i) => (
                        <tr key={c._id||c.id} onClick={() => navigate(`/cases/${c._id||c.id}`, { state: { tab: 'billing' } })}
                          style={{ borderTop:'1px solid var(--border)', cursor:'pointer', background:i%2===0?'transparent':'var(--bg-base)' }}
                          onMouseOver={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                          onMouseOut={e=>e.currentTarget.style.background=i%2===0?'transparent':'var(--bg-base)'}>
                          <td style={{ padding:'10px 12px', fontWeight:700, color:'var(--zeus-gold)' }}>{c.case_number||'—'}</td>
                          <td style={{ padding:'10px 12px', fontWeight:600 }}>{c.client_name||'—'}</td>
                          <td style={{ padding:'10px 12px' }}><span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:8, background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>{c.claim_type||'WC'}</span></td>
                          <td style={{ padding:'10px 12px' }}><span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:8, background:c.status==='settled'?'rgba(34,197,94,0.1)':'rgba(100,116,139,0.1)', color:c.status==='settled'?'#22c55e':'var(--text-muted)', textTransform:'capitalize' }}>{c.status||'—'}</span></td>
                          <td style={{ padding:'10px 12px', fontWeight:700, color:parseFloat(c.settlement_amount)>0?'#22c55e':'var(--text-muted)' }}>{parseFloat(c.settlement_amount)>0 ? fmtMoney(c.settlement_amount) : '—'}</td>
                          <td style={{ padding:'10px 12px', textAlign:'center' }}>{c.af_fee ? <span style={{ fontWeight:700 }}>{c.af_fee}%</span> : <span style={{ color:'var(--text-muted)' }}>—</span>}</td>
                          <td style={{ padding:'10px 12px', textAlign:'center' }}>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.75rem', display:'flex', alignItems:'center', gap:3, margin:'0 auto' }}
                              onClick={e=>{e.stopPropagation();navigate(`/cases/${c._id||c.id}`, { state: { tab: 'billing' } });}}>
                              Open <ChevronRight size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          </div>
        )}

      </div>
    </>
  );
}
